import type { WorkingFilters } from "components/types/working-filters";
import StorageHelper from "./storage-helper";
import Cookies, { type Cookie, type CookieSetOptions } from "universal-cookie";
import { COOKIES } from "rtr-constants";
import { isPropertyOf } from "components/types/type-utils";

/**
 * An interface to manage persisted filters between Next and Ruby.
 * Long-term we should only require the cookie portion of this for Next.
 * This historically was routed through Redux but we didn't actually use the store values.
 * Rather, we use these on the Next server and when initializing working filters in the browser.
 */
export default class PersistedFilters {
  readonly #reserveDiscoGridsEnabled: boolean;
  readonly #storageHelper: {
    setItem: (key: keyof WorkingFilters, value: unknown) => unknown;
    removeItem: (key: keyof WorkingFilters) => void;
  };
  readonly #cookies: Cookies;

  static isPersistedFilter(key: keyof WorkingFilters) {
    return Object.values(this.persistedFilters).includes(key);
  }

  static readonly persistedFilters = {
    availableOnly: "availableOnly",
    basicClosetMinAvailability: "rtrUpdateMinAvailability",
    date: "date",
    duration: "duration",
    fullClosetMinAvailability: "unlimitedMinAvailability",
    maternity: "epicMaternity",
    size: "canonicalSizes",
    zipCode: "zip_code",
  };

  static readonly workingFiltersToCookies: Record<keyof WorkingFilters, { name: string; options?: CookieSetOptions }> =
    {
      availableOnly: { name: COOKIES.AVAILABLE_ONLY },
      canonicalSizes: {
        name: COOKIES.CANONICAL_SIZES,
        options: { maxAge: 60 * 60 * 24 * 365 * 10 }, //10 Years
      },
      date: {
        name: COOKIES.RENT_BEGIN_DATE,
      },
      duration: { name: COOKIES.DURATION },
      zip_code: { name: COOKIES.ZIP_CODE },
    };

  static serializeFilter<T extends keyof WorkingFilters>(
    key: T,
    value: WorkingFilters[T]
  ): WorkingFilters[keyof WorkingFilters] {
    //sbenedict 6/25
    //We historically serialized canonicalSizes using a template literal
    //This leads to a value in the form 2,4
    //UniversalCookie uses JSON serialization, i.e. ["2", "4"] which breaks things
    if (key === "canonicalSizes") {
      if (!Array.isArray(value) || !value.length) {
        return;
      }

      return value.join(",");
    }

    return value;
  }

  /**
   * sbenedict 6/25
   * We only want to persist Reserve filters in a cookie for users in the treatment group for now.
   * This will allow us to control routing to Reserve grids on Next in a phased manner.
   */
  static mapWorkingFilterToCookie(key: keyof WorkingFilters, reserveDiscoGridsEnabled: boolean) {
    if (reserveDiscoGridsEnabled) {
      return isPropertyOf(this.workingFiltersToCookies, key) ? this.workingFiltersToCookies[key] : null;
    } else {
      return ["canonicalSizes", "availableOnly"].includes(key) ? this.workingFiltersToCookies[key] : null;
    }
  }

  constructor(reserveDiscoGridsEnabled: boolean) {
    this.#reserveDiscoGridsEnabled = reserveDiscoGridsEnabled;
    this.#storageHelper = StorageHelper.getApi("availability", [
      "unlimitedMinAvailability",
      "rtrUpdateMinAvailability",
    ]);
    this.#cookies = new Cookies();

    this.writePreference = this.writePreference.bind(this);
    this.writePreferences = this.writePreferences.bind(this);
    this.clearPreference = this.clearPreference.bind(this);
    this.clearPreferences = this.clearPreferences.bind(this);
  }

  writePreferences(updatedFilters: WorkingFilters) {
    Object.entries(updatedFilters).forEach(([key, value]) => this.writePreference(key, value));
  }

  writePreference<T extends keyof WorkingFilters>(key: T, value: WorkingFilters[T]) {
    if (!PersistedFilters.isPersistedFilter(key)) {
      return;
    }

    const cookie = PersistedFilters.mapWorkingFilterToCookie(key, this.#reserveDiscoGridsEnabled);

    if (cookie) {
      this.#writeCookie(cookie.name, value, cookie.options);
    }

    this.#storageHelper.setItem(key, value);
  }

  #writeCookie(key: string, value: Cookie, options: CookieSetOptions = {}) {
    const serializedValue = PersistedFilters.serializeFilter(key, value);

    //We don't want empty cookie values, as it can cause unintended behavior like initializing canonicalSizes as [undefined]
    if (typeof serializedValue === "undefined") {
      this.#deleteCookie(key);
      return;
    }

    this.#cookies.set(key, serializedValue, { ...options, path: "/" });
  }

  clearPreferences(keys: (keyof WorkingFilters)[]) {
    keys.forEach(k => this.clearPreference(k));
  }

  clearPreference(key: keyof WorkingFilters) {
    if (!PersistedFilters.isPersistedFilter(key)) {
      return;
    }

    const cookie = PersistedFilters.mapWorkingFilterToCookie(key, this.#reserveDiscoGridsEnabled);

    if (cookie) {
      this.#deleteCookie(cookie.name);
    }

    this.#storageHelper.removeItem(key);
  }

  #deleteCookie(key: string) {
    this.#cookies.remove(key);
  }
}
